"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Table,
  Badge,
  Progress,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap"
import { FaCalendarDay, FaUsers } from "react-icons/fa"
import { toast } from "react-toastify"
import PerformanceService from "../../../Service/performance-service"
import type { TodayPerformanceData, OverallPerformanceData } from "./performance-types"

interface Props {
  refreshTrigger: boolean
}

const PerformanceDashboardList: React.FC<Props> = ({ refreshTrigger }) => {
  const [todayPerformance, setTodayPerformance] = useState<TodayPerformanceData | null>(null)
  const [overallPerformance, setOverallPerformance] = useState<OverallPerformanceData | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("1")

  // Fetch today's performance
  const fetchTodayPerformance = async () => {
    try {
      const data = await PerformanceService.getTodayStaffPerformance()
      setTodayPerformance(data)
    } catch (error) {
      console.error("Failed to fetch today's performance:", error)
      toast.error("Failed to load today's performance data")
    }
  }

  // Fetch overall performance
  const fetchOverallPerformance = async () => {
    try {
      const data = await PerformanceService.getOverallStaffPerformance()
      setOverallPerformance(data)
    } catch (error) {
      console.error("Failed to fetch overall performance:", error)
      toast.error("Failed to load overall performance data")
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      await Promise.all([fetchTodayPerformance(), fetchOverallPerformance()])
      setLoading(false)
    }

    fetchData()
  }, [refreshTrigger])

  const toggle = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const getPerformanceColor = (percentage: number) => {
    if (percentage >= 80) return "success"
    if (percentage >= 60) return "warning"
    return "danger"
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Excellent":
        return "success"
      case "Good":
        return "warning"
      default:
        return "danger"
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Performance Tabs */}
      <Card>
        <CardHeader>
          <Nav tabs>
            <NavItem>
              <NavLink
                className={activeTab === "1" ? "active" : ""}
                onClick={() => toggle("1")}
                style={{ cursor: "pointer" }}
              >
                <FaCalendarDay className="me-2" />
                Today's Performance
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "2" ? "active" : ""}
                onClick={() => toggle("2")}
                style={{ cursor: "pointer" }}
              >
                <FaUsers className="me-2" />
                Overall Performance
              </NavLink>
            </NavItem>
          </Nav>
        </CardHeader>
        <CardBody>
          <TabContent activeTab={activeTab}>
            {/* Tab 1: Today's Performance */}
            <TabPane tabId="1">
              {todayPerformance && (
                <>
                  {/* Today's Totals */}
                  <Row className="mb-4">
                    <Col md="6">
                      <Card className="bg-success text-white">
                        <CardBody className="text-center">
                          <h4 className="mb-0">{todayPerformance.totals.total_used_today}</h4>
                          <small>Total Used Today</small>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md="6">
                      <Card className="bg-warning text-white">
                        <CardBody className="text-center">
                          <h4 className="mb-0">{todayPerformance.totals.total_unused_today}</h4>
                          <small>Total Unused Today</small>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  {/* Today's Staff Performance Table */}
                  <div className="table-responsive">
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>Service Member</th>
                          <th>Tickets Verified</th>
                          <th>Total Today</th>
                          <th>Date</th>
                          <th>Performance %</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {todayPerformance.staff_performance.map((staff) => (
                          <tr key={staff.user_id}>
                            <td>
                              <div>
                                <div className="fw-bold">{staff.person_name}</div>
                                <small className="text-muted">{staff.email}</small>
                              </div>
                            </td>
                            <td>
                              <Badge color="primary">{staff.tickets_verified}</Badge>
                            </td>
                            <td>
                              <Badge color="info">{staff.total_tickets_today}</Badge>
                            </td>
                            <td>{new Date(todayPerformance.date).toLocaleDateString()}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <Progress
                                  value={staff.percentage}
                                  color={getPerformanceColor(staff.percentage)}
                                  className="me-2"
                                  style={{ width: "60px", height: "8px" }}
                                />
                                <span>{staff.percentage.toFixed(1)}%</span>
                              </div>
                            </td>
                            <td>
                              <Badge color={getStatusColor(staff.status)}>{staff.status}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </TabPane>

            {/* Tab 2: Overall Performance */}
            <TabPane tabId="2">
              {overallPerformance && (
                <>
                  {/* Overall Totals */}
                  <Row className="mb-4">
                    <Col md="6">
                      <Card className="bg-success text-white">
                        <CardBody className="text-center">
                          <h4 className="mb-0">{overallPerformance.totals.total_used_all_days}</h4>
                          <small>Total Used All Days</small>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md="6">
                      <Card className="bg-warning text-white">
                        <CardBody className="text-center">
                          <h4 className="mb-0">{overallPerformance.totals.total_unused_all_days}</h4>
                          <small>Total Unused All Days</small>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  {/* Overall Staff Performance Table */}
                  <div className="table-responsive">
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Service Member</th>
                          <th>Total Verified</th>
                          <th>Total All Time</th>
                          <th>Performance %</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overallPerformance.staff_performance.map((staff, index) => (
                          <tr key={staff.user_id}>
                            <td>
                              <Badge color={index < 3 ? "warning" : "secondary"}>#{index + 1}</Badge>
                            </td>
                            <td>
                              <div>
                                <div className="fw-bold">{staff.person_name}</div>
                                <small className="text-muted">{staff.email}</small>
                              </div>
                            </td>
                            <td>
                              <Badge color="primary">{staff.tickets_verified}</Badge>
                            </td>
                            <td>
                              <Badge color="info">{staff.total_tickets_all}</Badge>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <Progress
                                  value={staff.percentage}
                                  color={getPerformanceColor(staff.percentage)}
                                  className="me-2"
                                  style={{ width: "60px", height: "8px" }}
                                />
                                <span>{staff.percentage.toFixed(1)}%</span>
                              </div>
                            </td>
                            <td>
                              <Badge color={getStatusColor(staff.status)}>{staff.status}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  )
}

export default PerformanceDashboardList
